import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ListOrdered, Car, Clock, Droplets, Wifi, WifiOff } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { useSocket, useSocketEvent } from '@/hooks/useSocket';
import { cn, formatRelativeTime, VEHICLE_LABELS, QUEUE_STATUS_LABELS } from '@/lib/utils';
import { Spinner } from '@/components/Spinner';
import EmptyState from '@/components/EmptyState';

interface QueueEntry {
  id: string;
  queueNumber: number;
  status: 'waiting' | 'washing' | 'ready' | 'completed' | 'cancelled';
  bayNumber: number | null;
  vehicleType: string;
  plateNumber: string;
  vehicleColor: string;
  vehicleBrand: string;
  customerName: string;
  customerPhone: string;
  joinedAt: string;
  washingStartedAt: string | null;
  readyAt: string | null;
  estimatedReadyAt: string | null;
  washDurationMinutes: number | null;
}

interface QueueData {
  entries: QueueEntry[];
  stats: {
    waiting: number;
    washing: number;
    ready: number;
    completed: number;
  };
  bayCount: number;
}

export default function QueuePage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { socket, isConnected } = useSocket(user?.shopId);
  const [filter, setFilter] = useState<string>('all');

  const { data, isLoading } = useQuery<QueueData>({
    queryKey: ['owner', 'queue'],
    queryFn: () => api.get('/owner/queue'),
    refetchInterval: isConnected ? 60000 : 10000,
  });

  // Listen for real-time queue updates — force immediate refetch
  useSocketEvent(socket, 'shop:queue_changed', () => {
    queryClient.refetchQueries({ queryKey: ['owner', 'queue'] });
  });

  useSocketEvent(socket, 'shop:bay_changed', () => {
    queryClient.refetchQueries({ queryKey: ['owner', 'queue'] });
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  const queue = data || { entries: [], stats: { waiting: 0, washing: 0, ready: 0, completed: 0 }, bayCount: 1 };

  const filtered = queue.entries.filter(entry => {
    if (filter === 'completed') return entry.status === 'completed';
    if (filter === 'waiting') return entry.status === 'waiting';
    if (filter === 'washing') return entry.status === 'washing';
    if (filter === 'ready') return entry.status === 'ready';
    return true;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">الطابور المباشر</h1>
          <p className="text-gray-500 text-sm mt-1">عرض للقراءة فقط</p>
        </div>
        <div className="flex items-center gap-2">
          {isConnected ? (
            <span className="flex items-center gap-1.5 text-xs text-green-600 bg-green-50 px-2.5 py-1 rounded-full">
              <Wifi className="w-3.5 h-3.5" />
              متصل
            </span>
          ) : (
            <span className="flex items-center gap-1.5 text-xs text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full">
              <WifiOff className="w-3.5 h-3.5" />
              غير متصل
            </span>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: 'بالانتظار', value: queue.stats.waiting, color: 'bg-amber-100 text-amber-700' },
          { label: 'يُغسل', value: queue.stats.washing, color: 'bg-blue-100 text-blue-700' },
          { label: 'جاهز', value: queue.stats.ready, color: 'bg-green-100 text-green-700' },
          { label: 'مكتمل', value: queue.stats.completed, color: 'bg-gray-100 text-gray-700' },
        ].map((stat) => (
          <div key={stat.label} className="bg-white rounded-xl p-3 shadow-card text-center">
            <p className="text-xl font-bold text-gray-900">{stat.value}</p>
            <span className={cn('text-xs px-2 py-0.5 rounded-full inline-block mt-1', stat.color)}>
              {stat.label}
            </span>
          </div>
        ))}
      </div>

      {/* Bay Status */}
      {queue.bayCount > 1 && (
        <div className="bg-white rounded-2xl p-5 shadow-card">
          <h2 className="text-base font-bold text-gray-900 mb-3">
            أماكن الغسيل ({queue.stats.washing}/{queue.bayCount})
          </h2>
          <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2">
            {Array.from({ length: queue.bayCount }, (_, i) => {
              const bay = i + 1;
              const entry = filtered.find(e => e.bayNumber === bay && e.status === 'washing');
              return (
                <div
                  key={bay}
                  className={cn(
                    'rounded-xl p-3 text-center border-2 transition-colors',
                    entry
                      ? 'bg-blue-50 border-blue-200'
                      : 'bg-gray-50 border-gray-200'
                  )}
                >
                  <p className="text-xs text-gray-500 mb-1">مكان {bay}</p>
                  {entry ? (
                    <>
                      <Car className="w-5 h-5 text-blue-600 mx-auto mb-1" />
                      <p className="text-xs font-medium text-gray-800 truncate">{entry.vehicleBrand}</p>
                      <p className="text-[10px] text-gray-500">{entry.plateNumber}</p>
                    </>
                  ) : (
                    <p className="text-xs text-gray-400 mt-2">فارغ</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="flex gap-1.5 bg-gray-100 p-1 rounded-xl">
        {[
          { key: 'all', label: 'الكل' },
          { key: 'waiting', label: 'بالانتظار' },
          { key: 'washing', label: 'يُغسل' },
          { key: 'ready', label: 'جاهز' },
          { key: 'completed', label: 'مكتمل' },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setFilter(tab.key)}
            className={cn(
              'flex-1 px-2 py-1.5 rounded-lg text-sm font-medium transition-colors',
              filter === tab.key ? 'bg-primary-600 text-white' : 'text-gray-600 hover:bg-gray-200'
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Queue Entries */}
      {filtered.length === 0 ? (
        <EmptyState icon={ListOrdered} title="لا توجد سيارات في الطابور" />
      ) : (
        <div className="space-y-3">
          {filtered.map((entry) => {
            const statusInfo = QUEUE_STATUS_LABELS[entry.status];
            return (
              <div key={entry.id} className="bg-white rounded-2xl p-4 shadow-card">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center">
                      <span className="text-sm font-bold text-gray-700">#{entry.queueNumber}</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {entry.vehicleBrand} {entry.vehicleColor && `- ${entry.vehicleColor}`}
                      </p>
                      <p className="text-xs text-gray-500">
                        {entry.plateNumber} - {VEHICLE_LABELS[entry.vehicleType]?.ar}
                      </p>
                      {entry.customerName && (
                        <p className="text-xs text-gray-400 mt-0.5">{entry.customerName}</p>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col items-end gap-1">
                    <span className={cn('text-xs px-2 py-0.5 rounded-full font-medium', statusInfo?.color)}>
                      {statusInfo?.ar}
                    </span>
                    {entry.bayNumber && entry.status === 'washing' && (
                      <span className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">
                        مكان {entry.bayNumber}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-4 mt-3 text-xs text-gray-400">
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    دخل {formatRelativeTime(entry.joinedAt)}
                  </span>
                  {entry.washingStartedAt && (
                    <span className="flex items-center gap-1">
                      <Droplets className="w-3 h-3" />
                      بدأ {formatRelativeTime(entry.washingStartedAt)}
                    </span>
                  )}
                  {entry.washDurationMinutes && (
                    <span className="flex items-center gap-1">
                      <Timer className="w-3 h-3" />
                      {entry.washDurationMinutes} د
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Timer(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <line x1="10" x2="14" y1="2" y2="2" /><line x1="12" x2="15" y1="14" y2="11" />
      <circle cx="12" cy="14" r="8" />
    </svg>
  );
}
